import React, { useState, useEffect, useRef } from 'react';

interface SliderProps {
  min: number;
  max: number;
  step: number;
  value: number;
  onChange: (value: number) => void;
  label: React.ReactNode;
  unit?: string;
  formatValue?: (value: number) => React.ReactNode;
  className?: string;
}

const Slider: React.FC<SliderProps> = ({
  min,
  max,
  step,
  value,
  onChange,
  label,
  unit = '',
  formatValue,
  className = '',
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [inputValue, setInputValue] = useState(value.toString());
  const sliderRef = useRef<HTMLDivElement>(null);
  
  // Keep input value in sync with prop value when not focused
  useEffect(() => {
    if (!isFocused) {
      setInputValue(value.toString());
    }
  }, [value, isFocused]);
  
  // Calculate percentage for slider position, ensuring it stays within bounds
  const percentage = Math.min(Math.max(((value - min) / (max - min)) * 100, 0), 100);
  
  const handleTrackClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (sliderRef.current) {
      const rect = sliderRef.current.getBoundingClientRect();
      const clickPosition = (e.clientX - rect.left) / rect.width;
      const newValue = min + clickPosition * (max - min);
      const steppedValue = Math.round(newValue / step) * step;
      onChange(Math.min(Math.max(steppedValue, min), max));
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    
    // Only update the slider if the input is a valid number
    if (newValue !== '') {
      const numberValue = Number(newValue);
      if (!isNaN(numberValue)) {
        onChange(numberValue);
      }
    }
  };

  const handleInputBlur = () => {
    setIsFocused(false);
    
    // On blur, enforce min/max limits
    let finalValue = value;
    if (value < min) {
      finalValue = min;
    } else if (value > max) {
      finalValue = max;
    }
    
    // Update both the input value and the slider
    setInputValue(finalValue.toString());
    onChange(finalValue);
  };

  const handleInputFocus = () => {
    setIsFocused(true);
  };

  const renderInput = () => {
    if (formatValue) {
      return formatValue(value);
    }
    
    return (
      <div className="flex items-center gap-1">
        <input
          type="number"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          className="w-24 px-2 py-1 text-secondary-500 font-medium border border-secondary-300 rounded focus:outline-none focus:ring-1 focus:ring-secondary-500 focus:border-secondary-500 bg-white"
          step={step}
        />
        {unit && <span>{unit}</span>}
      </div>
    );
  };

  return (
    <div className={`mb-6 ${className}`}>
      <div className="flex justify-between items-center mb-2">
        <label className="text-sm font-medium text-primary-700">{label}</label>
        <div className="text-secondary-500 font-medium">
          {renderInput()}
        </div>
      </div>
      
      <div 
        ref={sliderRef}
        className="relative h-2 bg-gray-200 rounded-full cursor-pointer"
        onClick={handleTrackClick}
      >
        <div 
          className="absolute h-full bg-secondary-500 rounded-full"
          style={{ width: `${percentage}%` }}
        />
        <div 
          className={`
            absolute h-5 w-5 rounded-full bg-white border-2 
            ${isFocused ? 'border-secondary-600 shadow-lg scale-110' : 'border-secondary-500'} 
            transform -translate-y-1/2 -translate-x-1/2 top-1/2
            transition-all duration-150 cursor-grab active:cursor-grabbing
          `}
          style={{ left: `${percentage}%` }}
          onMouseDown={() => setIsFocused(true)}
          onMouseUp={() => setIsFocused(false)}
          onMouseLeave={() => setIsFocused(false)}
        />
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => onChange(Math.max(Number(e.target.value), min))}
          className="absolute inset-0 w-full h-full appearance-none opacity-0 cursor-pointer"
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
        />
      </div>
      
      <div className="flex justify-between mt-1 text-xs text-gray-500">
        <span>{min}{unit}</span>
        <span>{max}{unit}</span>
      </div>
    </div>
  );
};

export default Slider;